import { useCallback, useEffect, useRef, useState } from 'react';
import PageTemplate from 'components/template/PageTemplate';
import Container from 'components/common/Container';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { jsonAxios } from 'components/axios';
import InfoArea from 'components/transport/detail/InfoArea';
import { toast } from 'react-toastify';
import { ISpecialBookingInfo, ISpecialData } from 'types/special';
import { countryCodeToEmoji, getKoreaDateTime } from 'components/function';
import { ReactComponent as Down } from 'assets/special/down.svg'
import CustomButton from 'components/common/CustomButton';
import { ReactComponent as Plus } from 'assets/special/plus.svg';
import { ReactComponent as Minus } from 'assets/special/minus.svg';
import ConfrimDialog from 'components/common/ConfrimDialog';
import InfoAreaMulti from 'components/special/InfoAreaMulti';
import DetailInfoArea from 'components/special/DetailInfoArea';
import CustomInput from 'components/common/CustomInput';
import HsCode from 'components/common/HsCode';
import HsCodeModal from 'components/common/modal/HsCodeModal';
import { hsCodePopAtom, hsCodeResultAtom } from 'atom/hsCode';
import { useAtom, useSetAtom } from 'jotai';
import useCheckCompany from 'hook/useCheckCompany';
import { sendTeamRoom } from 'components/nateon';
import { useGetUser, userAtom } from 'atom/user';
import { regexEnglish, regexKorean } from 'components/common/regex';
import { encrypt } from 'components/encryptDecrpt';

const SpecialDetail = () => {
    const navigate = useNavigate();
    const user = useGetUser();
    const setUser = useSetAtom(userAtom)
    const [searchParams] = useSearchParams();
    const id = searchParams.get('id');
    const handledNoId = useRef<boolean>(false);
    const [data, setData] = useState<ISpecialData>({} as ISpecialData);
    const [amount, setAmount] = useState<number>(1);
    const [popOpen, setPopOpen] = useState<boolean>(false);
    const [available, setAvailable] = useState<number>(0);
    const [bookingInfo, setBookingInfo] = useState<ISpecialBookingInfo>({} as ISpecialBookingInfo)
    const [popHsCodeOpen, setPoHsCodepOpen] = useAtom(hsCodePopAtom)
    const setHsCodeResult = useSetAtom(hsCodeResultAtom);
    const [clear, setClear] = useState<boolean>(false);

    useCheckCompany();

    const init = useCallback(() => {
        jsonAxios.get(`special-rates/${id}`).then(res => {
            if (res.data.code === 200) {
                setData(res.data.data)
            }
        }).catch(() => {
            toast.error('서버 오류가 발생했습니다.');
        });
    }, [id]);

    useEffect(() => {
        if (!id) {
            if (!handledNoId.current) {
                handledNoId.current = true;
                toast.error('잘못된 경로입니다.');
                setTimeout(() => {
                    navigate(-1);
                }, 500);
            }
            return;
        }

        if (user.id === undefined) return;
        checkUser((serUser) => {
            if (serUser.isVIP) {
                setHsCodeResult('');
                init();
                return
            }
            else {
                toast.error('해당 메뉴 이용 권한이 없습니다. 자세한 내용은 1:1 문의 주시기 바랍니다.')
                navigate('/', { replace: true })
                return;
            }
        });
    }, [id, user.id, init]);

    useEffect(() => {
        !!bookingInfo.hsCode &&
            !!bookingInfo.productNameEn &&
            !!bookingInfo.productNameKr ?
            setClear(true) : setClear(false)
    }, [bookingInfo])

    const checkUser = (fn: (serUser: any) => void) => {
        jsonAxios.get('user/info').then(res => {
            console.log(res.data)
            if (res.data.code === 200) {
                const serUser = res.data.data
                setUser(encrypt(JSON.stringify(res.data.data)))
                fn(serUser);
            }
        })
    }

    const saveSpeial = () => {
        const reqData = {
            freightId: id,
            quantity: available > 0 ? available : amount,
            ...bookingInfo
        }
        jsonAxios.post('special-rates/users', reqData).then(res => {
            if (res.data.code === 200) {
                sendTeamRoom('special', `📦 Pulse On Ship 특가운임 신청 🔥\n
신청일시: ${getKoreaDateTime()}
유저: ${user.username}
회사이름: ${user.companyInfo.companyNameKr}
운송사/항공사: ${data.carrierName}
품목정보 ${bookingInfo.productNameKr} | ${bookingInfo.productNameEn}
HS코드: ${bookingInfo.hsCode}
신청수량 ${(available > 0 ? available : amount).toLocaleString()}개
컨테이너 ${data.containerType} | ${data.containerWeight}kg
경로: ${countryCodeToEmoji(data.originCode.slice(0, 2))} ${data.originCode} | ${data.originAddress} > ${countryCodeToEmoji(data.destinationCode.slice(0, 2))} ${data.destinationCode} | ${data.destinationAddress}`)
                setPopOpen(false)
                toast.success('정상적으로 특가운임을 신청했습니다.')
                setAmount(1)
                setAvailable(0)
                navigate('/quote/status/list')

            }
            else if (res.data.code === 401) {
                if (res.data.data.remainedQuantity === 0) {
                    toast.error('해당 운임은 마감되었습니다.');
                    navigate(-1)
                }
                else {
                    toast.error('최대 주문 가능 수량을 초과했습니다.');
                    setAvailable(res.data.data.remainedQuantity)
                }
            }
            else if (res.data.code === 402) {
                toast.error('최대 주문 가능 수량을 초과했습니다.');
            }
        })
    }

    return (
        <PageTemplate background='bg-[#F8F8FA]'>
            <Container className="flex flex-col gap-6 tablet:gap-4 mb-6 tablet:py-4 pt-21 h-full min-h-dvh">
                <p className='font-semibold tablet:text-lg text-3xl'>특가운임</p>
                <div className='bg-white border-[#9D9EB7] border-1 rounded-lg'>
                    <div className='tablet:p-4 px-10 py-6 border-[#F8F8FA] border-b font-semibold'>
                        <p className='tablet:text-lg text-2xl'>세부내역</p>
                    </div>

                    {
                        data &&
                        data.destinationCode &&
                        <div className='relative flex tablet:flex-col'>
                            <div className='flex flex-col flex-1 gap-6 tablet:p-4 px-10 py-6'>
                                <p className='font-semibold tablet:text-lg text-2xl'>입력정보</p>
                                <DetailInfoArea title={`품목명(한글) <span class='text-red'> *</span>`} isTag className='bg-[#F8F8FA] rounded h-12'>
                                    <CustomInput
                                        height={10}
                                        type='text'
                                        placeholder='품목명(한글)을 입력해주세요'
                                        value={bookingInfo.productNameKr ?? ''}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (regexKorean(value) || value === "") {
                                                setBookingInfo({
                                                    ...bookingInfo,
                                                    productNameKr: e.target.value
                                                })
                                            }
                                        }}
                                    />
                                </DetailInfoArea>
                                <DetailInfoArea title={`품목명(영문) <span class='text-red'> *</span>`} isTag className='bg-[#F8F8FA] rounded h-12'>
                                    <CustomInput
                                        height={10}
                                        type='text'
                                        placeholder='품목명(영문)을 입력해주세요'
                                        value={bookingInfo.productNameEn ?? ''}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (regexEnglish(value) || value === "") {
                                                setBookingInfo({
                                                    ...bookingInfo,
                                                    productNameEn: e.target.value
                                                })
                                            }
                                        }}
                                    />
                                </DetailInfoArea>

                                <DetailInfoArea title={`HS CODE <span class='text-red'> *</span>`} isTag className='bg-[#F8F8FA] rounded h-12'>
                                    <HsCode
                                        hsCode={bookingInfo.hsCode}
                                        width='w-full'
                                        fontSize='base'
                                        onChange={(value) => {
                                            setBookingInfo({
                                                ...bookingInfo,
                                                hsCode: value
                                            })
                                        }}
                                    />
                                </DetailInfoArea>

                                <DetailInfoArea title={`타겟 레이트`} className='bg-[#F8F8FA] rounded h-12'>
                                    <CustomInput
                                        height={10}
                                        type='text'
                                        placeholder='타겟 레이트를 입력해주세요.'
                                        value={bookingInfo.rate ?? ''}
                                        onChange={(e) => {
                                            setBookingInfo({
                                                ...bookingInfo,
                                                rate: e.target.value
                                            })
                                        }}
                                    />
                                </DetailInfoArea>
                                <hr />
                                <p className='font-semibold text-2xl'>특가운임 정보</p>
                                <InfoArea
                                    title='출발지'
                                    value={`${data.originCode} | ${data.originAddress}`}
                                />
                                {
                                    data.isVesselInfoVisible &&
                                    data.transitInfo &&
                                    data.transitInfo.length > 0 &&
                                    data.transitInfo[0].transitId &&
                                    <InfoAreaMulti
                                        title='경유지'
                                        value={
                                            data.transitInfo.map((item, idx) => (
                                                `<div class="flex justify-between items-center">
                                                    <p><span class="font-bold">${item.transitCode}</span> | ${item.transitAddress}</p>
                                                    <span class='flex justify-center items-center bg-primary rounded-full w-4.5 h-4.5 font-bold text-white text-xs'>${idx + 1}</span>
                                                </div>`
                                            ))
                                        }
                                        row={data.transitInfo.length}
                                    />
                                }
                                <InfoArea
                                    title='도착지'
                                    value={`${data.destinationCode} | ${data.destinationAddress}`}
                                />
                                {
                                    data.isVesselInfoVisible &&
                                    <InfoAreaMulti
                                        title='스케줄'
                                        value={
                                            [
                                                `<span><span class="font-bold">ETD</span> | ${data.etd}</span>`,
                                                `<span><span class="font-bold">ETA</span> | ${data.eta}</span>`,
                                            ]
                                        }
                                        row={2}
                                    />
                                }
                                <InfoArea
                                    title={'선박명'}
                                    value={data.vesselName}
                                />
                                <InfoArea
                                    title='물류물품'
                                    value={data.item}
                                />
                                <InfoArea
                                    title="운임"
                                    value={`${data.currency} ${data.freightRate?.toLocaleString()} / ${data.containerType}${data.containerWeight !== null ? ` / ${Number(data.containerWeight).toLocaleString()}Kg` : ''}`}
                                />
                                <InfoArea
                                    title='유효기간'
                                    value={`~ ${data.validTo} 까지`}
                                />
                                {
                                    data.description &&
                                    <InfoArea
                                        title='설명'
                                        value={data.description}
                                    />
                                }

                            </div>
                            <div className='top-18 sticky flex-1 tablet:p-4 px-10 py-6 rounded-xl h-full'>
                                <div className='bg-white'>
                                    <div className='border border-[#9D9EB7] rounded-lg'>
                                        <div className='px-6 py-4 border-[#F8F8FA] border-b font-semibold tablet:text-lg text-2xl'>
                                            {data.carrierName}
                                        </div>
                                        <div className='flex flex-col justify-between items-start gap-4 px-6 py-4'>
                                            <div className='flex flex-1 items-center gap-2'>
                                                <p className='text-42 flag'>{countryCodeToEmoji(data.originCode.slice(0, 2))}</p>
                                                <div className='text-lg'>
                                                    <p className='font-semibold leading-none'>{data.originCode}</p>
                                                    <p className='font-semibold leading-none'>{data.originAddress}</p>
                                                    {
                                                        data.isVesselInfoVisible &&
                                                        <p className='text-base'>{data.etd}(ETD)</p>
                                                    }
                                                </div>
                                            </div>
                                            <Down className='self-center' />
                                            <div className='flex flex-1 items-center gap-2'>
                                                <p className='text-42 flag'>{countryCodeToEmoji(data.destinationCode.slice(0, 2))}</p>
                                                <div className='text-lg'>
                                                    <p className='font-semibold leading-none'>{data.destinationCode}</p>
                                                    <p className='font-semibold leading-none'>{data.destinationAddress}</p>
                                                    {
                                                        data.isVesselInfoVisible &&
                                                        <p className='text-base'>{data.eta}(ETA)</p>
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className='flex tablet:flex-col justify-between items-end tablet:items-start gap-1 py-6 border-[#D9D9D9] border-b'>
                                        <div className="flex justify-between bg-[#fff] border border-[#999999] rounded h-10 overflow-hidden text-black">
                                            <div
                                                className="flex flex-1 justify-center items-center bg-[#F6F6F6] border-[#999999] border-r-1"
                                                onClick={() => {
                                                    setAmount(p => Math.max(1, p - 1));
                                                }}
                                            >
                                                <Minus />
                                            </div>
                                            <p
                                                className="flex flex-[2] justify-center items-center font-medium">
                                                <input
                                                    className="bg-transparent w-full h-full text-lg text-center"
                                                    value={amount}
                                                    type="number"
                                                    readOnly
                                                    min={0}
                                                    onChange={() => { }}
                                                />
                                            </p>
                                            <div
                                                className="flex flex-1 justify-center items-center bg-[#F6F6F6] border-[#999999] border-l-1"
                                                onClick={() => {
                                                    setAmount(p => p + 1);
                                                }}
                                            >
                                                <Plus />
                                            </div>
                                        </div>
                                        <div className='flex items-end gap-3'>
                                            <p className='text-lg'>특가운임</p>
                                            <p className='font-bold tablet:text-lg text-2xl'>{data.currency} {(data.freightRate * amount)?.toLocaleString()}</p>
                                        </div>
                                    </div>

                                    <div className='py-6'>
                                        <CustomButton
                                            disabled={!clear}
                                            className='bg-primary w-full'
                                            title='부킹하기'
                                            onClick={() => {
                                                checkUser((serUser) => {
                                                    if (serUser.isVIP) {
                                                        setPopOpen(true)
                                                        return
                                                    }
                                                    else {
                                                        toast.error('해당 메뉴 이용 권한이 없습니다. 자세한 내용은 1:1 문의 주시기 바랍니다.')
                                                        navigate('/', { replace: true })
                                                        return;
                                                    }
                                                });
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </Container >
            <ConfrimDialog
                isOpen={popOpen}
                setIsOpen={setPopOpen}
                btnText='확인'
                isTablet
                cancelAction={() => {
                    setAvailable(0)
                    setPopOpen(false)
                }}
                action={saveSpeial}
                cancelColor='bg-[#9D9EB7]'
            >
                <div className='flex flex-col gap-3 tablet:text-base text-lg'>
                    {
                        popOpen &&
                        <div className='flex flex-col gap-3'>
                            <div className='font-semibold text-center'>
                                {data?.carrierName}
                            </div>
                            <div className='flex items-center gap-3'>
                                <p className='w-20'>· 출발 :</p>
                                <p className='text-32 flag'>{countryCodeToEmoji(data?.originCode.slice(0, 2))}</p>
                                <div>
                                    <p className='font-semibold leading-none'>{data?.originCode} {data?.originAddress}</p>
                                    {
                                        data?.isVesselInfoVisible &&
                                        <p className='text-base'>{data?.etd}(ETD)</p>
                                    }
                                </div>
                            </div>

                            {
                                data?.isVesselInfoVisible &&
                                data?.transitInfo &&
                                data?.transitInfo.length > 0 &&
                                data?.transitInfo[0].transitId &&
                                data?.transitInfo.map((item, idx) => (
                                    <div className='flex items-center gap-3' key={idx}>
                                        <p className='w-20'>· 경유지 :</p>
                                        <p className='text-32 flag'>{countryCodeToEmoji(item?.transitCode.slice(0, 2))}</p>
                                        <p><span className="font-bold">{item.transitCode}</span> | {item.transitAddress}</p>
                                        <span className='flex justify-center items-center bg-primary rounded-full w-4.5 h-4.5 font-bold text-white text-xs shrink-0'>{idx + 1}</span>
                                    </div>
                                ))
                            }
                            <div className='flex items-center gap-3'>
                                <p className='w-20'>· 도착 :</p>
                                <p className='text-32 flag'>{countryCodeToEmoji(data?.destinationCode.slice(0, 2))}</p>
                                <div>
                                    <p className='font-semibold leading-none'>{data?.destinationCode} {data?.destinationAddress}</p>
                                    {
                                        data?.isVesselInfoVisible &&
                                        <p className='text-base'>{data?.eta}(ETA)</p>
                                    }
                                </div>
                            </div>
                        </div>
                    }
                    <p>· 품목명(한글) : <span className='font-semibold'>{bookingInfo.productNameKr}</span></p>
                    <p>· 품목명(영문) : <span className='font-semibold'>{bookingInfo.productNameEn}</span></p>
                    <p>· HS CODE : <span className='font-semibold'>{bookingInfo.hsCode}</span></p>
                    {
                        available > 0 &&
                        <p>· 현재 부킹 가능 수량 : <span className='font-semibold text-red'>{available} CNTR {data?.containerType}</span></p>
                    }
                    <p>· 부킹 요청 수량 : <span className='font-semibold'>{amount} CNTR {data?.containerType}</span></p>
                    <p>· 특가 운임 : <span className='font-semibold'>{data?.currency} {(data?.freightRate * amount)?.toLocaleString()}</span></p>

                    <div className='mt-3'>
                        {
                            available > 0 ?
                                <div className='font-semibold text-red'>
                                    <p>최대 주문 가능 수량을 초과했습니다.</p>
                                    <p>
                                        <span>‘{available} CNTR {data?.containerType}’</span>
                                        <span className='font-medium text-black'>만 부킹을 진행하시겠습니까?</span>
                                    </p>
                                    <p>· 특가 운임 : {data?.currency} {(data?.freightRate * available)?.toLocaleString()}</p>
                                </div> :
                                <p> 위 내용으로 부킹 예약을 하시겠습니까?</p>
                        }
                    </div>
                </div>
            </ConfrimDialog>

            <HsCodeModal
                isOpen={popHsCodeOpen}
                setIsOpen={setPoHsCodepOpen}
            />
        </PageTemplate>
    )
}

export default SpecialDetail